import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import SerialNumberReference from './SerialNumberReference';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import {
  Button,
  ButtonIcon,
  CloudUploadIcon,
  Input,
  SkeletonInputGrid,
  Toggle,
  TrashIcon,
} from '@rabbit/elements/shared-components';
import {
  FileStorageContext,
  VendableEditorContext,
} from '@rabbit/bizproc/react';
import {
  AddEditVendableFormValuesShape,
  DocTypeShapeTypes,
  FileUploaderVariations,
  UploadedFileCategories,
} from '@rabbit/elements/shared-types';
import {
  DTVendable,
  DTVendable_Private,
  DTWarranty_Template,
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
  UserUploadedDocument,
  VendableFull,
} from '@rabbit/data/types';
import { t } from 'i18next';
import { SageFileUploader } from '../../upload-wrapper/SageFileUploader';
import Skeleton from 'react-loading-skeleton';
import { v4 as uuid } from 'uuid';
import { AppContext } from '@rabbit/app-context';
import { REGISTRATION_FIELDS } from '@rabbit/config/enums';
import { supagardBrandCategories } from '../../ModalNewEditPartner/ModalNewEditPartner';
import { PlusIcon } from '@heroicons/react/24/outline';
import { convertUploadImage } from '@rabbit/sage/utils/helpers.tsx';

export interface ModalAddEditVendableProps {
  handleClose: () => void;
  //handleDelete?: () => void;
  vendableId?: string;
  setLoading: Dispatch<SetStateAction<boolean>>;
  data?: any;
  onChangeStep?: (step_label: string, data: any) => void;
}

export function ModalAddEditVendableV2({
  handleClose,
  //handleDelete,
  setLoading,
  vendableId,
  data,
  onChangeStep,
}: ModalAddEditVendableProps) {
  const [validateOnChange, setValidateOnChange] = useState<boolean>(false);
  const activePremiumTenant = t('tenantLink');
  const formikRef = useRef<FormikProps<AddEditVendableFormValuesShape> | null>(
    null
  );
  const saveAndAddAnotherRef = useRef<boolean>(false);
  const { config, tenantInfo } = useContext(AppContext) || {};
  const isUniqueProductActivated =
    config.HOLDINGS.NEW_REGISTRATION_FLOW.UNIQUE_PRODUCT;
  const [newVendableIdState, setNewVendableIdState] = useState<string | undefined>(
    !vendableId ? `${activePremiumTenant}_${uuid()}` : undefined
  );
  const [formKey, setFormKey] = useState<number>(0);

  const contextValues = useContext(VendableEditorContext);
  const {
    createOrUpdateVendable,
    getSingleVendable,
    getWarrantyTemplates,
    setShouldRefresh,
  } = contextValues;
  const {
    unusedCompletedCleanup,
    moveCompletedUploadsToAttached,
    deleteFile,
    uploadQueueState,
  } = useContext(FileStorageContext) || {};

  const [vendableData, setVendableData] = useState<VendableFull | null>(null);
  const [isImageUploaded, setIsImageUploaded] = useState<boolean>(false);
  const [warrantyTemplates, setWarrantyTemplates] = useState<
    DTWarranty_Template[]
  >([]);
  const [parsedSerialNumbers, setParsedSerialNumbers] = useState<string[]>([]);
  const pendingSubmissionRef = useRef<AddEditVendableFormValuesShape | null>(
    null
  );
  const warrantyPlanOptions =
    generateWarrantyTemplateOptions(warrantyTemplates);

  const defaultBrandName = t('CFG_COBRAND_NAME', '') ?? '';

  /* ------------------------------- Fetch data ------------------------------- */
  useEffect(() => {
    if (vendableId && getSingleVendable) {
      getSingleVendable(vendableId).then((data) => {
        setVendableData(data);
        setIsImageUploaded(
          !!data?.pub?.images?.length || !!data?.pub?.img?.length
        );

        // Parse CSV file if it exists
        if (data?.pub?.serialNumbersCsv) {
          parseCSVFile(data.pub.serialNumbersCsv);
        }
      });
    }

    getWarrantyTemplates().then((data) => {
      setWarrantyTemplates(data);
    });
  }, [vendableId]);

  /* -------------------- Clean up unused completed uploads ------------------- */
  useEffect(() => {
    return () => {
      if (unusedCompletedCleanup) {
        void unusedCompletedCleanup();
      }
    };
  }, []);

  useEffect(() => {
    if (pendingSubmissionRef.current) {
      const values = pendingSubmissionRef.current;
      pendingSubmissionRef.current = null; // Clear pending reference
      onSubmit(values); // Proceed with submission
    }
  }, [uploadQueueState]);

  // Function to parse CSV file
  const parseCSVFile = useCallback(
    async (csvFile: UserUploadedDocument) => {
      try {
        const response = await fetch(csvFile.url);
        const text = await response.text();

        // Split by newline and filter out empty lines
        const serialNumbers = text
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line.length > 0);

        setParsedSerialNumbers(serialNumbers);
      } catch (error) {
        console.error('Error parsing CSV file:', error);
        toast.error(t('message.errorParsingCsvFile'));
      }
    },
    [t]
  );

  // Watch for changes to the serialNumbersCsv field
  useEffect(() => {
    const formikInstance = formikRef.current;
    if (formikInstance?.values.serialNumbersCsv) {
      parseCSVFile(formikInstance.values.serialNumbersCsv);
    } else {
      setParsedSerialNumbers([]);
    }
  }, [uploadQueueState, parseCSVFile]);

  /* -------------------------------------------------------------------------- */
  /*                              Handle submission                             */
  /* -------------------------------------------------------------------------- */
  // Function to compare alphanumeric strings
  const compareAlphanumeric = (a: string, b: string) => {
    // Natural sort comparison for alphanumeric strings
    return a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  };

  // Function to check if a serial number is within a range
  const isSerialNumberInRange = (
    serialNumber: string,
    range: { start: string; end: string }
  ) => {
    if (!range.start || !range.end) return false;
    return (
      compareAlphanumeric(serialNumber, range.start) >= 0 &&
      compareAlphanumeric(serialNumber, range.end) <= 0
    );
  };

  // Function to check if a serial number already exists in the list
  const isSerialNumberDuplicate = (
    serialNumber: string,
    serialNumbers: string[]
  ) => {
    return serialNumbers.includes(serialNumber);
  };

  // Function to check if a range overlaps with individual serial numbers
  const doesRangeOverlapWithSerialNumbers = (
    range: { start: string; end: string },
    serialNumbers: string[]
  ) => {
    if (!range.start || !range.end) return false;
    return serialNumbers.some((sn) => isSerialNumberInRange(sn, range));
  };

  // Function to check if two ranges overlap
  const doRangesOverlap = (
    range1: { start: string; end: string },
    range2: { start: string; end: string }
  ) => {
    if (!range1.start || !range1.end || !range2.start || !range2.end)
      return false;

    // Check if range1 starts within range2
    const range1StartsInRange2 =
      compareAlphanumeric(range1.start, range2.start) >= 0 &&
      compareAlphanumeric(range1.start, range2.end) <= 0;

    // Check if range1 ends within range2
    const range1EndsInRange2 =
      compareAlphanumeric(range1.end, range2.start) >= 0 &&
      compareAlphanumeric(range1.end, range2.end) <= 0;

    // Check if range2 is completely within range1
    const range2WithinRange1 =
      compareAlphanumeric(range2.start, range1.start) >= 0 &&
      compareAlphanumeric(range2.end, range1.end) <= 0;

    return range1StartsInRange2 || range1EndsInRange2 || range2WithinRange1;
  };

  // Function to check if a range overlaps with any existing ranges
  const doesRangeOverlapWithExistingRanges = (
    range: { start: string; end: string },
    ranges: { start: string; end: string }[],
    currentIndex: number
  ) => {
    if (!range.start || !range.end) return false;

    return ranges.some((existingRange, index) => {
      // Skip comparing with itself
      if (index === currentIndex) return false;
      return doRangesOverlap(range, existingRange);
    });
  };

  const onSubmit = async (values: AddEditVendableFormValuesShape) => {
    // console.log('values', values);
    setLoading(true);
    if (uploadQueueState?.ongoing.length) {
      pendingSubmissionRef.current = values; // Queue the submission
      return;
    }

    // Only process serial number fields if the feature is enabled and the checkbox is checked
    if (
      config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED &&
      values.enableSerialNumberValidation
    ) {
      // Ensure serialNumbers is an array
      if (!values.serialNumbers) {
        values.serialNumbers = [];
      }

      // Ensure serialNumberRanges is an array and has Symbol.iterator
      if (!values.serialNumberRanges) {
        values.serialNumberRanges = [];
      }

      // Add Symbol.iterator method to ensure it's iterable
      if (
        values.serialNumberRanges &&
        !values.serialNumberRanges[Symbol.iterator]
      ) {
        Object.defineProperty(values.serialNumberRanges, Symbol.iterator, {
          enumerable: false,
          configurable: true,
          writable: true,
          value: Array.prototype[Symbol.iterator],
        });
      }

      // Validate serial numbers and ranges
      const serialNumbers = values.serialNumbers || [];
      const ranges = values.serialNumberRanges || [];

      // Check for duplicate serial numbers
      const findDuplicates = (arr: string[]) => {
        const seen = new Set<string>();
        const duplicates = new Set<string>();

        arr.forEach((item) => {
          if (seen.has(item)) {
            duplicates.add(item);
          } else {
            seen.add(item);
          }
        });

        return Array.from(duplicates);
      };

      const duplicateSerialNumbers = findDuplicates(serialNumbers);
      if (duplicateSerialNumbers.length > 0) {
        toast.error(
          t('Duplicate serial numbers found: ') +
            duplicateSerialNumbers.join(', ')
        );
        setLoading(false);
        return;
      }

      // Check if any serial number is within a range
      for (const serialNumber of serialNumbers) {
        for (const range of ranges) {
          if (isSerialNumberInRange(serialNumber, range)) {
            toast.error(t('Serial number is within an existing range'));
            setLoading(false);
            return;
          }
        }
      }

      // Check for overlapping ranges
      for (let i = 0; i < ranges.length; i++) {
        const range = ranges[i];
        if (doesRangeOverlapWithSerialNumbers(range, serialNumbers)) {
          toast.error(t('Range overlaps with existing serial numbers'));
          setLoading(false);
          return;
        }
        if (doesRangeOverlapWithExistingRanges(range, ranges, i)) {
          toast.error(t('Range overlaps with another range'));
          setLoading(false);
          return;
        }
      }
    } else if (
      config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED &&
      !values.enableSerialNumberValidation
    ) {
      // If the feature is enabled but the checkbox is unchecked, clear the serial number fields
      values.serialNumbers = [];
      values.serialNumberRanges = [];
      values.serialNumbersCsv = undefined;
    }
    let uploadedFiles = values.vendableImages;

    if (uploadQueueState?.completed.length)
      uploadedFiles = uploadQueueState?.completed.map(
        (file) => file.uploadedFile
      ) as UserUploadedDocument[];
    const pubVendable: Partial<DTVendable> = {
      title: values.title,
      // We'll not be doing any differentiation between title and full title for now
      full: values.title,
      detail: values.detail,
      brand: values.brand,
      img: isImageUploaded
        ? uploadedFiles?.map((image) => image?.url as string)
        : [],
      images: isImageUploaded ? uploadedFiles : [],
      upc: values.upc,
      mpn: values.sku,
      unique: isUniqueProductActivated ? values.unique : false,
      mfr:
        PersonaTypeSingleLetter.Manufacturer +
        PersonaIdTypeSplitter +
        activePremiumTenant,
      category: values.category ?? [], // TODO: integrate full category system
      keywords: values.keywords,
      defaultTemplate: values.warrantyTemplate,
      deprecated: values.deprecated === 'true' ? true : false,
      manual: values.manual,
      published: values.published,
      ...(config.VENDABLES.CUSTOM_FIELDS[
        REGISTRATION_FIELDS.CHASSIS_NUMBER
      ] && { chassis_number: values.chassis_number }),
      ...(config.VENDABLES.CUSTOM_FIELDS[
        REGISTRATION_FIELDS.REGISTRATION_NUMBER
      ] && { registration_number: values.registration_number ?? '' }),
      ...(config.VENDABLES.CUSTOM_FIELDS[
        REGISTRATION_FIELDS.PRODUCTION_DATE
      ] && { production_date: values.production_date }),
      ...(config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED &&
      values.enableSerialNumberValidation
        ? {
            serialNumbers: values.serialNumbers,
            serialNumberRanges: values.serialNumberRanges,
            serialNumbersCsv: values.serialNumbersCsv,
            serialNumberEnabled: true,
          }
        : {
            serialNumberEnabled: false,
          }),
    };
    const rrp = {
      amount: Number(values.rrp?.amount) ?? 0,
      currency: values.rrp?.currency ?? 'AUD',
    };

    const privVendable: Partial<DTVendable_Private> = {
      mfr:
        PersonaTypeSingleLetter.Manufacturer +
        PersonaIdTypeSplitter +
        activePremiumTenant,
      ...(config.VENDABLES.CUSTOM_FIELDS.RRP && {
        rrp: rrp,
      }),
      published: values.published,
      deprecated: values.deprecated === 'true' ? true : false,
    };
    try {
      const payload = {
        publicData: pubVendable,
        privateData: privVendable,
        vendableId,
        newVendableId: newVendableIdState,
      };
      const docId = await createOrUpdateVendable(payload);
      if (!onChangeStep) {
        setShouldRefresh(2000);
      }

      moveCompletedUploadsToAttached?.(uploadQueueState?.completed ?? []);
      // delete all attached vendable image files that aren't the new one.
      // (manual can have multiple)
      // This is to prevent orphaned files in the file storage
      // when we enable multiple images per vendable this needs to be reviewed
      if (vendableData?.pub.images && values.vendableImages.length > 0) {
        const filesToBeCleared = vendableData.pub.images.filter(
          (file) => file.url !== values.vendableImages[0].url
        );
        if (filesToBeCleared.length > 0 && deleteFile) {
          for (const file of filesToBeCleared) {
            void deleteFile(file.url, {
              category: UploadedFileCategories.VendableImages,
            });
          }
        }
      }
      toast.success(t('message.productRegisteredSuccessfully'));
      if (!onChangeStep) {
        if (!vendableId && saveAndAddAnotherRef.current) {
          // Prepare for adding another product
          setShouldRefresh(1000);
          setIsImageUploaded(false);
          setParsedSerialNumbers([]);
          // Regenerate new vendable id for next product
          setNewVendableIdState(`${activePremiumTenant}_${uuid()}`);
          // Reset the form to initial blank values
          const blankValues = {
            ...initialValues,
            title: '',
            detail: '',
            upc: '',
            sku: '',
            category: [],
            keywords: [],
            manual: [],
            vendableImages: [],
            published: false,
            ...(config.VENDABLES.CUSTOM_FIELDS?.DEPRECATED
              ? { deprecated: 'false' as any }
              : {}),
            ...(config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED
              ? {
                  serialNumbers: [],
                  serialNumberRanges: [],
                  serialNumbersCsv: undefined,
                  newSerialNumber: '',
                  newRangeStart: '',
                  newRangeEnd: '',
                  enableSerialNumberValidation: false,
                }
              : {}),
          } as AddEditVendableFormValuesShape;
          formikRef.current?.resetForm({ values: blankValues });
          setFormKey((k) => k + 1);
          saveAndAddAnotherRef.current = false;
          toast.info(t('Ready to add another product'));
        } else {
          handleClose();
        }
      }
      if (onChangeStep) {
        onChangeStep('add-product', {
          ...data,
          productResponse: {
            id: docId,
            label: values.title + ' (' + values.sku + ')',
            value: docId,
          },
        });
      }
    } catch (err) {
      console.error(err);
      toast.error(
        `${t('Something went wrong while')} ${
          newVendableIdState ? t('creating') : t('editing')
        } ${t('the product! Please try again later.')}`
      );
    } finally {
      pendingSubmissionRef.current = null;
      setLoading(false);
    }
  };
  /* -------------------------------------------------------------------------- */
  /*                                 Form setup                                 */
  /* -------------------------------------------------------------------------- */

  // These are the fields we will be going with for the editor. For non-Shopify based tenants,
  // they will all be editable. For Shopify based ones, however, some will be read-only, and
  // some will be editable

  const schema = Yup.object().shape({
    title: Yup.string().required(t('message.pleaseEnterATitle')),
    detail: Yup.string(),
    brand: Yup.string().required(t('message.pleaseEnterABrand')),
    ...(config.VENDABLES.MANDATORY_FIELDS.UPC
      ? { upc: Yup.string().required(t('message.pleaseEnterUPC')) }
      : { upc: Yup.string() }),
    sku: Yup.string().required(t('Required')),
    category: Yup.array().min(1).required(t('message.pleaseSelectACategory')),
    // disabled for first release -dc
    // keywords: Yup.array(),
    warrantyTemplate: Yup.string().required(t('Required')),
    ...(config.VENDABLES.CUSTOM_FIELDS.RRP && {
      rrp: Yup.object({
        amount: Yup.number().required(t('Required')),
        currency: Yup.string().required(t('Required')),
      }),
    }),
    manual: Yup.array(),
    // todo type this to UserUploadedDocument[] -dc
    vendableImages: Yup.array(),
    published: Yup.boolean().required(t('Required')),
    unique: Yup.boolean().default(false),
    // this is later forced into boolean, but has to be string to due to some weird input behaviours
    ...(config.VENDABLES.CUSTOM_FIELDS.DEPRECATED && {
      deprecated: Yup.string().required(t('Required')),
    }),
    ...(config.VENDABLES.CUSTOM_FIELDS[REGISTRATION_FIELDS.CHASSIS_NUMBER] && {
      chassis_number: Yup.string().required(t('Required')),
    }),
    ...(config.VENDABLES.CUSTOM_FIELDS[
      REGISTRATION_FIELDS.REGISTRATION_NUMBER
    ] && {
      registration_number: Yup.string().required(t('Required')),
    }),
    ...(config.VENDABLES.CUSTOM_FIELDS[REGISTRATION_FIELDS.PRODUCTION_DATE] && {
      production_date: Yup.date().required(t('Required')),
    }),
    ...(config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED && {
      serialNumbers: Yup.array().of(Yup.string()),
      serialNumberRanges: Yup.array().of(
        Yup.object().shape({
          start: Yup.string().required(t('Required')),
          end: Yup.string().required(t('Required')),
        })
      ),
      serialNumbersCsv: Yup.object(),
      // Temporary fields for UI interaction
      newSerialNumber: Yup.string(),
      newRangeStart: Yup.string(),
      newRangeEnd: Yup.string(),
      // Toggle for serial number validation
      enableSerialNumberValidation: Yup.boolean(),
    }),
  });

  const initialValues: AddEditVendableFormValuesShape = {
    title: vendableData?.pub.title ?? '',
    brand: vendableData?.pub?.brand ?? defaultBrandName,
    detail: vendableData?.pub?.detail ?? '',
    upc: vendableData?.pub?.upc ?? '',
    sku: vendableData?.pub?.mpn ?? '',
    // todo these need to match the category system -dc
    category: vendableData?.pub.category ?? [],
    keywords: vendableData?.pub.keywords ?? [],
    warrantyTemplate: vendableData?.pub.defaultTemplate ?? '',
    ...(config.VENDABLES.CUSTOM_FIELDS.RRP
      ? {
          rrp: vendableData?.priv.rrp ?? {
            amount: 0,
            currency: tenantInfo?.currency ?? 'GBP',
          },
        }
      : {}),
    manual: vendableData?.pub.manual ?? [],
    vendableImages:
      vendableData?.pub.images ?? vendableData?.pub.img
        ? convertUploadImage(vendableData?.pub.img ?? [])
        : [],
    published: vendableData?.pub.published ?? false,
    unique: vendableData?.pub.unique
      ? vendableData?.pub.unique
      : onChangeStep
      ? true
      : false,
    ...(config.VENDABLES.CUSTOM_FIELDS.DEPRECATED
      ? {
          deprecated: vendableData?.pub.deprecated ? 'true' : 'false',
        }
      : {}),
    ...(config.VENDABLES.CUSTOM_FIELDS[REGISTRATION_FIELDS.CHASSIS_NUMBER] && {
      chassis_number: vendableData?.pub.chassis_number ?? '',
    }),
    ...(config.VENDABLES.CUSTOM_FIELDS[
      REGISTRATION_FIELDS.REGISTRATION_NUMBER
    ] && { registration_number: vendableData?.pub.registration_number ?? '' }),
    ...(config.VENDABLES.CUSTOM_FIELDS[REGISTRATION_FIELDS.PRODUCTION_DATE] && {
      production_date: vendableData?.pub.production_date
        ? new Date(vendableData?.pub.production_date!)
        : new Date(),
    }),
    ...(config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED && {
      serialNumbers: vendableData?.pub.serialNumbers ?? [],
      // Initialize serialNumberRanges and ensure it has Symbol.iterator
      serialNumberRanges: (() => {
        const ranges = vendableData?.pub.serialNumberRanges ?? [];
        if (!ranges[Symbol.iterator]) {
          Object.defineProperty(ranges, Symbol.iterator, {
            enumerable: false,
            configurable: true,
            writable: true,
            value: Array.prototype[Symbol.iterator],
          });
        }
        return ranges;
      })(),
      serialNumbersCsv: vendableData?.pub.serialNumbersCsv ?? undefined,
      // Temporary fields for UI interaction
      newSerialNumber: '',
      newRangeStart: '',
      newRangeEnd: '',
      // Toggle for serial number validation
      enableSerialNumberValidation: vendableData?.pub.serialNumberEnabled,
    }),
  };

  // Ref controls for the file uploader input
  const selectFileRef = useRef<HTMLElement>();
  const removeFileRef = useRef<HTMLElement>();

  /* -------------------------------------------------------------------------- */
  /*                                     TSX                                    */
  /* -------------------------------------------------------------------------- */
  if (!newVendableIdState && !vendableData)
    return (
      <div className="p-5">
        {/** This is pretty unique to this modal, but if used again extract to a separate component */}
        <div className="mb-5 grid grid-cols-4 gap-2 rounded-md border border-gray-200 p-2">
          <div className="col-span-1">
            <Skeleton count={1} className="py-14" />
          </div>
          <div className="col-span-3 py-3 ">
            <div className="mb-4 grid grid-cols-2 gap-2">
              <Skeleton count={1} className="py-2" />
              <Skeleton count={1} className="!w-1/2 py-2" />
            </div>
            <Skeleton count={1} className="!w-1/3 py-2" />
          </div>
          <div className="col-span-4">
            <Skeleton count={1} className="py-3" />
          </div>
        </div>
        <SkeletonInputGrid cols={2} rows={5} />
      </div>
    );

  const getCategories = () =>
    t('tenantLink') === 'TRANSPORTER'
      ? TransporterCategories
      : config.HOLDINGS.PRODUCT.CUSTOM_CATEGORY
      ? SaezCategories
      : isUniqueProductActivated
      ? AbbysJeweleryCategories
      : SheltaCategories;

  return (
    <div>
      <Formik
        key={formKey}
        innerRef={formikRef}
        validationSchema={schema}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validateOnChange={validateOnChange}
        validateOnBlur={true}
      >
        {(props) => {
          return (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                props.validateForm();
                setValidateOnChange(true);
                props.handleSubmit(e);
              }}
            >
              <div className="flex flex-col gap-4 px-4 pt-4">
                <div className="flex flex-col gap-4 rounded-lg border border-gray-200 p-2">
                  <div className="flex justify-start gap-4">
                    <div>
                      <SageFileUploader
                        variation={FileUploaderVariations.InputPicture}
                        name={'vendableImages'}
                        identifiers={{
                          personaId:
                            PersonaTypeSingleLetter.Manufacturer +
                            PersonaIdTypeSplitter +
                            activePremiumTenant,
                          category: UploadedFileCategories.VendableImages,
                          docType: {
                            type: DocTypeShapeTypes.Vendable,
                            docid: vendableId ?? newVendableIdState ?? null,
                          },
                        }}
                        currentFiles={props.values.vendableImages ?? []}
                        refs={{ selectFileRef, removeFileRef }}
                        maxFiles={1}
                      />
                    </div>
                    <div className="flex flex-col gap-4 py-4">
                      <div className="flex gap-2">
                        <div className="text-lg font-bold">
                          {vendableData?.pub.title ?? 'Product name'}
                        </div>
                        <div>
                          {vendableData?.pub.published ? (
                            <div className="inline overflow-hidden rounded bg-green-100 py-1 px-2">
                              {t('Published')}
                            </div>
                          ) : (
                            <div className="inline overflow-hidden rounded bg-gray-100 py-1 px-2">
                              {t('Unpublished')}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        {props.values.vendableImages.length === 0 && (
                          <ButtonIcon
                            kind="bgLightGreen"
                            onClick={() => {
                              selectFileRef.current?.click();
                              setIsImageUploaded(true);
                            }}
                            label={t('Upload image')}
                            Icon={CloudUploadIcon}
                            iconLeft
                          />
                        )}
                        {props.values.vendableImages.length > 0 && (
                          <ButtonIcon
                            kind="bgRed"
                            onClick={() => {
                              removeFileRef.current?.click();
                              setIsImageUploaded(false);
                            }}
                            label={t('Remove image')}
                            Icon={TrashIcon}
                            iconLeft
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 px-3 py-2">
                    <div className="grow">{t('Publish product')}</div>
                    <Toggle
                      name="publish"
                      checked={props.values.published}
                      onChange={() => {
                        props.setFieldValue(
                          'published',
                          !props.values.published
                        );
                      }}
                    />
                  </div>
                  {isUniqueProductActivated && (
                    <div className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 px-3 py-2">
                      <div className="grow">{t('Unique product')}</div>
                      <Toggle
                        name="unique"
                        checked={props.values.unique || false}
                        onChange={() => {
                          props.setFieldValue('unique', !props.values.unique);
                        }}
                        disabled={!!onChangeStep}
                      />
                    </div>
                  )}
                  {config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED && (
                    <div className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 px-3 py-2">
                      <div className="grow">{t('general.serialNumbers')}</div>
                      <Toggle
                        name="enableSerialNumberValidation"
                        checked={
                          props.values.enableSerialNumberValidation || false
                        }
                        onChange={() => {
                          props.setFieldValue(
                            'enableSerialNumberValidation',
                            !props.values.enableSerialNumberValidation
                          );
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      type="text"
                      label={t('Product name') + '*'}
                      name="title"
                      settings={{
                        hint: '*' + t('required'),
                        placeholder: t('Product name'),
                        allowSpecialCharacter: true,
                        allowedSpecialCharacters: [`'`],
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    {config.VENDABLES.BRAND_SELECTABLE ? (
                      <Input
                        type="select"
                        label={t('Brand') + '*'}
                        name="brand"
                        settings={{
                          options: supagardBrandCategories,
                          placeholder: t('general.pleaseSelectABrand'),
                          id: 'brand',
                          errors: props.errors,
                          hint: '*' + t('required'),
                        }}
                      />
                    ) : (
                      <Input
                        type="text"
                        label={t('Brand') + '*'}
                        name="brand"
                        settings={{
                          hint: '*' + t('required'),
                          placeholder: t('Brand name'),
                          allowSpecialCharacter: true,
                          allowedSpecialCharacters: [`'`],
                        }}
                      />
                    )}
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-1">
                    {/* Meanwhile, we use this one */}
                    <Input
                      type="select"
                      label={t('Category') + '*'}
                      name="category"
                      settings={{
                        isMulti: true,
                        options: getCategories(),
                        placeholder: t('general.pleaseSelectACategory'),
                        id: 'category',
                        errors: props.errors,
                        hint: '*' + t('required'),
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      type="text"
                      label={t('SKU') + '*'}
                      name="sku"
                      settings={{
                        hint: '*' + t('required'),
                        placeholder: t('SKU'),
                      }}
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      type="text"
                      label={
                        t('UPC') +
                        `${config.VENDABLES.MANDATORY_FIELDS.UPC ? '*' : ''}`
                      }
                      name="upc"
                      settings={{
                        hint: config.VENDABLES.MANDATORY_FIELDS.UPC
                          ? '*' + t('required')
                          : '',
                        placeholder: t('UPC'),
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      type="select"
                      label={t('Warranty plan') + '*'}
                      name="warrantyTemplate"
                      settings={{
                        options: warrantyPlanOptions,
                        placeholder: t('message.pleaseSelectAnOption'),
                        id: 'warranty_plan',
                        errors: props.errors,
                      }}
                    />
                  </div>
                </div>
                {(config.VENDABLES.CUSTOM_FIELDS[
                  REGISTRATION_FIELDS.CHASSIS_NUMBER
                ] ||
                  config.VENDABLES.CUSTOM_FIELDS[
                    REGISTRATION_FIELDS.REGISTRATION_NUMBER
                  ]) && (
                  <div className="flex gap-4">
                    {config.VENDABLES.CUSTOM_FIELDS[
                      REGISTRATION_FIELDS.CHASSIS_NUMBER
                    ] && (
                      <div className="flex-1">
                        <Input
                          type="text"
                          label={`${t('general.chassis_number')}*`}
                          name="chassis_number"
                          settings={{
                            id: 'chassis_number',
                            placeholder: 'Enter chassis number',
                          }}
                        />
                      </div>
                    )}
                    {config.VENDABLES.CUSTOM_FIELDS[
                      REGISTRATION_FIELDS.REGISTRATION_NUMBER
                    ] && (
                      <div className="flex-1">
                        <Input
                          type="text"
                          label={`${t('general.registration_number')}*`}
                          name="registration_number"
                          settings={{
                            id: 'registration_number',
                            placeholder: 'Enter registration number',
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-4">
                  {config.VENDABLES.CUSTOM_FIELDS[
                    REGISTRATION_FIELDS.PRODUCTION_DATE
                  ] && (
                    <div className="flex-1">
                      <Input
                        type="datepicker"
                        name="production_date"
                        label={`${t('general.production_date')}*`}
                        settings={{
                          id: 'production_date',
                          placeholder: 'DD/MM/YYYY',
                        }}
                      />
                    </div>
                  )}
                  {config.VENDABLES.CUSTOM_FIELDS.RRP && (
                    <div className="flex-1">
                      <Input
                        type="currency"
                        label={t('general.rrp')}
                        name="rrp"
                        settings={{
                          id: 'rrp',
                          currency:
                            props?.values?.rrp?.currency ??
                            tenantInfo?.currency,
                        }}
                      />
                    </div>
                  )}
                  {config.VENDABLES.CUSTOM_FIELDS.DEPRECATED && (
                    <div className="flex-1">
                      <Input
                        type="select"
                        label={t('Deprecated')}
                        name="deprecated"
                        settings={{
                          // For some reason I can never assign booleans or falsy values here.
                          // They'll turn into an empty string instead of false, or 0, or whatever
                          // So I'm using "true" and "false" instead.
                          options: [
                            { value: 'true', label: t('Yes') },
                            { value: 'false', label: t('No') },
                          ],
                          placeholder: t('Deprecated'),
                          id: 'deprecated',
                          errors: props.errors,
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="">
                  <Input
                    type="rich-text"
                    label={t('Product description')}
                    name="detail"
                    settings={{
                      shouldValidate: false,
                    }}
                  />
                </div>
                <SageFileUploader
                  variation={FileUploaderVariations.FormItem}
                  name={'manual'}
                  label={t('Product documentation')}
                  identifiers={{
                    personaId:
                      PersonaTypeSingleLetter.Manufacturer +
                      PersonaIdTypeSplitter +
                      activePremiumTenant,
                    category: UploadedFileCategories.VendableManuals,
                    docType: {
                      type: DocTypeShapeTypes.Vendable,
                      docid: vendableId ?? newVendableIdState ?? null,
                    },
                  }}
                  currentFiles={props.values.manual ?? []}
                />

                {/* Serial Numbers Section */}
                {config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED &&
                  props.values.enableSerialNumberValidation && (
                    <SerialNumberReference
                      props={props}
                      vendableId={vendableId}
                      newVendableId={newVendableIdState}
                      activePremiumTenant={activePremiumTenant}
                      parsedSerialNumbers={parsedSerialNumbers}
                    />
                  )}
                <div className="sticky bottom-0 bg-white py-2">
                  {!vendableId ? (
                    <div className="flex gap-3">
                      <Button
                        kind="primary"
                        type="submit"
                        children={t('Save')}
                        disabled={!props.isValid || !props.dirty}
                        className="w-1/2"
                        onClick={() => {
                          saveAndAddAnotherRef.current = false;
                        }}
                      />
                      <Button
                        kind="primary"
                        type="button"
                        children={t('Save and add another')}
                        disabled={!props.isValid || !props.dirty}
                        className="w-1/2"
                        onClick={() => {
                          saveAndAddAnotherRef.current = true;
                          props.submitForm();
                        }}
                      />
                    </div>
                  ) : (
                    <Button
                      kind="primary"
                      type="submit"
                      children={t('Save')}
                      disabled={!props.isValid || !props.dirty}
                      className="w-full"
                      onClick={() => {
                        saveAndAddAnotherRef.current = false;
                      }}
                    />
                  )}
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

function generateWarrantyTemplateOptions(
  warrantyTemplates: DTWarranty_Template[]
) {
  return warrantyTemplates.map((template) => ({
    id: template.orderNo,
    value: template.docid,
    label: template.title,
  }));
}

const getValidCategories = (categories: string[]): string[] => {
  if (!categories || !Array.isArray(categories)) return [];
  const validCategories = categories
    .filter((category) =>
      SheltaCategories.some(
        (sheltaCategory) => sheltaCategory.value === category
      )
    )
    .map((category) => {
      const matchingCategory = SheltaCategories.find(
        (sheltaCategory) => sheltaCategory.value === category
      );
      return matchingCategory ? matchingCategory.label : undefined;
    })
    .filter((label): label is string => label !== undefined);

  return validCategories.length > 0 ? validCategories : [];
};

// Remove this when the category system is implemented
const SheltaCategories = [
  { label: 'Fashion and lifestyle', value: 'fashion-and-lifestyle' },
  { label: 'Clothing', value: 'clothing' },
  { label: 'Clothing accessories', value: 'clothing-accessories' },
  { label: 'Umbrellas', value: 'umbrellas' },
  { label: 'Home', value: 'home' },
  { label: 'Garden and patio', value: 'garden-and-patio' },
  {
    label: 'Outdoor and patio furniture',
    value: 'outdoor-and-patio-furniture',
  },
  { label: 'Patio umbrellas', value: 'patio-umbrellas' },
  { label: 'Generic', value: 'generic' },
  { label: 'Sports and recreation', value: 'sports-and-recreation' },
  { label: 'Camping, tourism, outdoor', value: 'camping-tourism-outdoor' },
  { label: 'Beach umbrellas', value: 'beach-umbrellas' },
];

const AbbysJeweleryCategories = [
  { label: 'Rings', value: 'rings' },
  { label: 'Bridal Rings', value: 'bridal-rings' },
  { label: 'Engagement Rings', value: 'engagement-rings' },
  { label: 'Ladies Diamond Rings', value: 'ladies-diamond-rings' },
  { label: 'Ladies Diamond Bands', value: 'ladies-diamond-bands' },
  { label: 'Men’s Diamond Rings', value: 'mens-diamond-rings' },
  { label: 'Men’s Diamond Bands', value: 'mens-diamond-bands' },
  { label: 'Wraps and Enhancers', value: 'wraps-and-enhancers' },
  { label: 'Stackables', value: 'stackables' },
  { label: 'Trio Sets', value: 'trio-sets' },
  { label: 'Bracelets', value: 'bracelets' },
  { label: 'Ladies Diamond Bracelets', value: 'ladies-diamond-bracelets' },
  { label: 'Men’s Diamond Bracelets', value: 'mens-diamond-bracelets' },
  { label: 'Earrings', value: 'earrings' },
  { label: 'Ladies Diamond Earrings', value: 'ladies-diamond-earrings' },
  { label: 'Studs', value: 'studs' },
  { label: 'Hoops', value: 'hoops' },
  { label: 'Necklaces', value: 'necklaces' },
  { label: 'Ladies Diamond Necklaces', value: 'ladies-diamond-necklaces' },
  { label: 'Men’s Diamond Necklaces', value: 'mens-diamond-necklaces' },
  { label: 'Pendants', value: 'pendants' },
  { label: 'Ladies Diamond Pendants', value: 'ladies-diamond-pendants' },
  { label: 'Men’s Crosses & Charms', value: 'mens-crosses-charms' },
  { label: 'Color Stones', value: 'color-stones' },
  { label: 'Tanzanite', value: 'tanzanite' },
  { label: 'Morganite', value: 'morganite' },
];

const SaezCategories = [
  { label: 'Air Distribution', value: 'Air Distribution' },
  { label: 'Motor Accessories', value: 'motor-accessories' },
  { label: 'HVAC Equipment', value: 'hvac-equipment' },
  { label: 'Testing & Tools', value: 'testing-tools' },
  { label: 'HVAC Parts', value: 'hvac-parts' },
  { label: 'Installation', value: 'installation' },
  { label: 'Motors', value: 'motors' },
  { label: 'Refrigeration', value: 'refrigeration' },
  { label: 'Supplies', value: 'supplies' },
  { label: 'Fire Detection Parts', value: 'fire-detection-parts' },
  { label: 'Thermostats', value: 'thermostats' },
  { label: 'Ventilation & IAQ', value: 'ventilation-iaq' },
  { label: 'Air/Evap Coil', value: 'Air/Evap Coil' },
  { label: 'Board', value: 'Board' },
  { label: 'Compressor', value: 'Compressor' },
  { label: 'Condenser Water Coil', value: 'Condenser Water Coil' },
  { label: 'Condensate Flow Sensor', value: 'Condensate Flow Sensor' },
  { label: 'Evaporator Units', value: 'Evaporator Units' },
  { label: 'Heat Pump Condenser Units', value: 'Heat Pump Condenser Units' },
  { label: 'Condenser Units', value: 'Condenser Units' },
  { label: 'Air Handling Units', value: 'Air Handling Units' },
  { label: 'Warranty Parts', value: 'Warranty Parts' },
  { label: 'Mini-Split Indoor Units', value: 'Mini-Split Indoor Units' },
  { label: 'Mini-Split Outdoor Units', value: 'Mini-Split Outdoor Units' },
  { label: 'Heat Pump Systems', value: 'Heat Pump Systems' },
  { label: 'Motor', value: 'Motor' },
];
const TransporterCategories = [
  { label: 'Axle & Suspension', value: 'axle-suspension' },
  { label: 'EVO8', value: 'EVO8' },
  { label: 'Odyssey Range', value: 'odyssey' },
  { label: 'Voyager Range', value: 'voyager-range' },
  { label: 'Urban Rigid Range', value: 'urban-rigid-range' },
  { label: 'Juno Range', value: 'juno-range' },
  { label: 'Factory Rebuild', value: 'factory-rebuild' },
];
